import React, { useCallback, useEffect, useState } from 'react';
import { Box, Paper, Typography, Grid, CircularProgress, Alert, Chip, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { VendorBadge, vendorLabel, VENDOR_SUPPORT, siteIdentifiers } from './vendorMeta';
import EnergyFlowDiagram from './EnergyFlowDiagram';
import { get, post } from '../../utils/api';

const kwh = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} kWh`;
const kw = (w) => `${(Number(w || 0) / 1000).toLocaleString('en-US', { maximumFractionDigits: 2 })} kW`;
const statusColor = (s) => (s === 'fault' ? 'error' : s === 'warning' ? 'warning' : s === 'offline' ? 'default' : 'success');
const statusLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown');

// Shared site-detail content (no page chrome). Used by the fleet dialog and the deep-link page.
export default function SolarSiteDetail({ siteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr(''); setLoading(true);
    try { setData(await get(`/api/manage/solar/site/${siteId}`)); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  // On-demand: pull this one building's live readings from its vendor now (not a full fleet sweep).
  const refreshNow = useCallback(async () => {
    setErr(''); setRefreshing(true);
    try { setData(await post(`/api/manage/solar/site/${siteId}/refresh`, {})); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Refresh failed'); }
    finally { setRefreshing(false); }
  }, [siteId]);

  const site = data?.site;
  const daily = (data?.daily || []).map((d) => ({ day: d.day.slice(8), kwh: Number(d.kwh || 0) }));
  const monthTotal = daily.reduce((a, d) => a + d.kwh, 0);
  const best = daily.reduce((m, d) => (d.kwh > m ? d.kwh : m), 0);
  const avg = daily.length ? monthTotal / daily.length : 0;

  const tile = (label, value) => (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.3 }}>{value}</Typography>
    </Paper>
  );
  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={26} /></Box>;
  if (err) return <Alert severity="error">{err}</Alert>;
  if (!site) return <Alert severity="warning">Site not found.</Alert>;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VendorBadge vendor={site.vendor} showLabel={false} size={28} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {site.name || site.building || site.label}
              {site.building && site.name ? <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>{site.building}</Typography> : null}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {site.address ? `${site.address} · ` : ''}{vendorLabel(site.vendor)}{site.capacity_kw ? ` · ${site.capacity_kw} kW` : ''}
          </Typography>
          {site.label && site.label !== (site.name || site.building) && (
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.3 }}>
              {vendorLabel(site.vendor)} portal calls this: “{site.label}”
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={refreshNow} disabled={refreshing}
              startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
            <Chip color={statusColor(site.status)} label={statusLabel(site.status)} />
          </Box>
          {(() => {
            const ts = site.updated_at;
            const stale = ts ? (Date.now() - new Date(ts).getTime()) / 3600000 > 26 : true;
            return (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: stale ? 'warning.main' : 'text.secondary', fontWeight: stale ? 700 : 400 }}>
                {ts ? `Updated ${new Date(ts).toLocaleString()}${stale ? ' · stale' : ''}` : 'No reading recorded yet'}
              </Typography>
            );
          })()}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>{tile('Producing now', site.power_w != null ? kw(site.power_w) : '—')}</Grid>
        <Grid item xs={6} md={3}>{tile('Today', kwh(site.today_kwh))}</Grid>
        <Grid item xs={6} md={3}>{tile('This month', site.month_kwh != null ? kwh(site.month_kwh) : '—')}</Grid>
        <Grid item xs={6} md={3}>{tile('Lifetime', Number(site.lifetime_kwh || 0) >= 1000 ? `${(site.lifetime_kwh / 1000).toFixed(2)} MWh` : kwh(site.lifetime_kwh))}</Grid>
      </Grid>

      {site.has_battery && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>Energy flow · live</Typography>
          <EnergyFlowDiagram
            solarW={site.power_w}
            gridW={site.grid_w}
            battW={site.batt_w}
            loadW={site.load_w}
            soc={site.battery_soc}
            inverterLabel={vendorLabel(site.vendor)}
          />
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', textAlign: 'center' }}>
            Arrows show the direction power is flowing right now · Grid positive = importing from MLGW, negative = exporting
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mt: 2, mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Daily production — this month</Typography>
        {daily.length > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            avg {avg.toFixed(1)} · best {best.toFixed(1)} · total {monthTotal.toFixed(0)} kWh
          </Typography>
        )}
      </Box>
      {daily.length === 0 ? (
        <Alert severity="info">No daily history yet for this month.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={daily} margin={{ top: 8, right: 12, bottom: 4, left: 0 }} barSize={16}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} tickMargin={6} />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} kWh`, 'Production']} labelFormatter={(l) => `Day ${l}`} />
              <Bar dataKey="kwh" fill="#EF9F27" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {(() => {
        const sup = VENDOR_SUPPORT[site.vendor] || {};
        const ids = siteIdentifiers(site);
        return (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {vendorLabel(site.vendor)} support · have these ready when you call
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <Box>
                {sup.phone && <Line label="Phone" value={sup.phone} />}
                {sup.email && <Line label="Email" value={sup.email} />}
                {sup.email2 && <Line label="Email (tech)" value={sup.email2} />}
                {sup.loginUrl && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Portal</Typography>
                    <Typography variant="caption" sx={{ textAlign: 'right', fontWeight: 600 }}>
                      <a href={sup.loginUrl} target="_blank" rel="noopener noreferrer">{sup.portal || 'Login'} ↗</a>
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box>
                {ids.map(([label, value]) => <Line key={label} label={label} value={value} mono />)}
              </Box>
            </Box>
          </Paper>
        );
      })()}
    </>
  );
}

function Line({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word',
        fontFamily: mono ? 'monospace' : undefined, userSelect: 'all' }}>{value}</Typography>
    </Box>
  );
}
